<script setup>
import { ref, computed, onMounted, watch } from "vue";

const content = await import("./mobility-content.json");

useHead({
  title: content.meta_tags.title,
  meta: content.meta_tags.meta,
   link: [
    { rel: "preload", as: "image", href: content.header.image }
  ]
});

definePageMeta({
  layout: "services-layout",
});

const search = ref("");
const workerResults = ref(content.services.item || []);
let worker = null;
let debounceTimer = null;

onMounted(() => {
  worker = new Worker(new URL("./fuseWorker.js", import.meta.url), {
    type: "module",
  });

  worker.onmessage = (e) => {
    const { type, items } = e.data;
    if (type === "results") {
      workerResults.value = items;
    }
  };

  worker.postMessage({
    type: "init",
    payload: { items: content.services.item || [] },
  });
});

watch(search, (newQuery) => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    if (newQuery.trim()) {
      worker.postMessage({ type: "search", payload: { query: newQuery } });
    } else {
      workerResults.value = content.services.item || [];
    }
  }, 300);
});

const filteredServices = computed(() => workerResults.value);

const highlightMatches = (text, searchTerm) => {
  if (!searchTerm.trim()) return text;
  const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedSearch})`, "gi");
  return text.replace(regex, '<span class="search-highlight">$1</span>');
};

const clearSearch = () => {
  search.value = "";
};
</script>

<template>
  <section class="py-32 relative">
     <img 
    :src="content.header.image" 
    :alt="content.header.heading"
    class="absolute inset-0 w-full h-full object-cover object-center z-0"
   >
    <div class="max-w-screen-xl mx-auto px-4 relative z-10">
      <h1 class="md:text-6xl leading-tight">
        <WrGradientText
          append-class="font-bold leading-[1.2] bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-3.57%,_var(--text-gradient-2)_25.72%)]"
        >
          {{ content.header.subheading }}<br />
          {{ content.header.heading }}
        </WrGradientText>
      </h1>
    </div>
  </section>

  <!-- Enhanced Search Bar -->
  <div class="max-w-screen-xl mx-auto px-4 mt-10">
    <div class="relative">
      <input
        v-model="search"
        type="text"
        :placeholder="content.services.placeholder_text"
        class="w-full px-14 py-2.5 md:py-4 rounded-full md:text-xl text-[var(--foreground)] focus:font-medium placeholder:text-[#958E9F] dark:!bg-transparent !border border-[#b5b5b5] dark:!border-[var(--primary)] shadow-sm focus:outline-none focus:ring-1 focus:ring-[#501d71] focus:border-transparent transition-all duration-300"
      />
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>

      <!-- Clear search button -->
      <button
        v-if="search.trim()"
        @click="clearSearch"
        class="absolute right-5 top-1/2 -translate-y-1/2 w-6 h-6 text-[#958E9F] hover:text-[#501d71] transition-colors duration-200"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <!-- Search Results Info -->
    <div v-if="search.trim()" class="mt-4 text-sm text-[#958E9F]">
      <span v-if="filteredServices.length > 0">
        Found {{ filteredServices.length }} solution{{
          filteredServices.length !== 1 ? "s" : ""
        }}
        matching "{{ search }}"
      </span>
      <span v-else class="text-orange-600">
        No solutions found matching "{{ search }}". Try different keywords.
      </span>
    </div>
  </div>

  <section
    class="relative z-20 overflow-hidden grid place-items-center max-w-screen-xl mx-auto px-4 my-10"
  >
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 py-2">
      <WrCardIndex
        v-for="(card, index) in filteredServices"
        :key="index"
        :card="card"
        :search-term="search"
      />
    </div>

    <!-- Empty state -->
    <div
      v-if="search.trim() && filteredServices.length === 0"
      class="text-center py-12"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="w-20 h-20 text-gray-300 mx-auto mb-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <h3 class="text-xl font-semibold text-gray-600 mb-2">
        No solutions found
      </h3>
      <p class="text-gray-500 mb-4">
        Try searching with different keywords like "SaaS", "eCommerce", "CMS",
        or "Development"
      </p>
      <WrButton @click="clearSearch" container-class="mx-auto">
        View All Solutions
      </WrButton>
    </div>
  </section>

  <section class="relative z-10 py-10">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="text-center">
        <WrGradientText
          text="Trusted for excellence and efficiency"
          append-class="text-3xl lg:text-[48px]"
        />
        <p class="text-lg text-[var(--foreground)]">
          Webreinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
      </h2>
      <WrTestimonialCardCarousel class="mt-12" />
      <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
        >View All</WrButton
      >
    </div>
  </section>
</template>
